import { useState, useEffect } from "react";
import { GoogleMap, InfoWindow, LoadScript, Marker } from "@react-google-maps/api";
import { FaMapMarkerAlt } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import { postFetcher } from "../../../api/fetchers";
import { endpoints } from "../../../api/endpoints";
import { useLanguage } from "../../context/LanguageContext";

/*const fakeStores = [
  { id: 1, lat: 41.043668, lng: 29.004673, name: "Novamed Store 1", city: "Istanbul", phone: '+90 532 111 22 33' },
  { id: 2, lat: 40.969416, lng: 29.105376, name: "Novamed Store 2", city: "Istanbul", phone: '+90 533 222 33 44' },
  { id: 3, lat: 40.983140, lng: 29.114774, name: "Novamed Store 3", city: "Istanbul", phone: '+90 534 333 44 55' },
  { id: 4, lat: 41.007996, lng: 29.180318, name: "Novamed Store 4", city: "Istanbul", phone: '+90 535 444 55 66' },
  { id: 5, lat: 41.005494, lng: 29.183011, name: "Novamed Store 5", city: "Istanbul", phone: '+90 536 555 66 77' },
  { id: 6, lat: 41.006789, lng: 29.174814, name: "Novamed Store 6", city: "Istanbul", phone: '+90 537 666 77 88' },
  { id: 7, lat: 41.005006, lng: 29.185555, name: "Novamed Store 7", city: "Istanbul", phone: '+90 538 777 88 99' },
  { id: 8, lat: 41.003212, lng: 29.181393, name: "Novamed Store 8", city: "Istanbul", phone: '+90 539 888 99 00' },
  { id: 9, lat: 40.998566, lng: 29.180634, name: "Novamed Store 9", city: "Istanbul", phone: '+90 530 999 00 11' },
  { id: 10, lat: 41.008777, lng: 29.187474, name: "Novamed Store 10", city: "Istanbul", phone: '+90 531 000 11 22' },
  { id: 11, lat: 41.004425, lng: 29.181596, name: "Novamed Store 11", city: "Istanbul", phone: '+90 532 123 45 67' },
];*/

const MapComponent = () => {

  const { t } = useLanguage();
  const [filteredStores, setFilteredStores] = useState<any>([]);
  const [userLocation, setUserLocation] = useState<string>("");
  const [searchCity, setSearchCity] = useState<string>("");
  const [mapCenter, setMapCenter] = useState({ lat: 54.5, lng: 15.2 });
  const [selectedStore, setSelectedStore] = useState<any>(null);

  useEffect(() => {
    if (userLocation) {
      const [latitude, longitude] = userLocation.split(",").map(Number);
      getStoresNearby(latitude, longitude);
    }
  }, [userLocation]);

  useEffect(() => {
    const fetchAddresses = async () => {
      const hasAllAddresses = filteredStores.every((store: any) => store.address);
      if (hasAllAddresses) return;

      const storesWithAddresses = await Promise.all(
        filteredStores.map(async (store: any) => {
          if (!store.address) {
            const address = await getAddressFromCoordinates(store.lat, store.lng);
            return { ...store, address };
          }
          return store;
        })
      );
      setFilteredStores(storesWithAddresses);
    };

    if (filteredStores.length > 0) {
      fetchAddresses();
    }
  }, [filteredStores]);


  const getAddressFromCoordinates = async (lat: number, lng: number) => {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_MAPKEY}`
    );
    const data = await response.json();
    if (data.status === "OK") {
      return data.results[0]?.formatted_address || "No address found";
    } else {
      return "No address found";
    }
  };


  const getCoordinatesFromCity = async (city: string) => {
    const [userLat, userLng] = userLocation.split(",").map(Number);

    if (!userLat || !userLng) {
      toast.error(t("maps", "text1"));
      return;
    }

    if (!city) return;

    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${city}&key=${process.env.REACT_APP_MAPKEY}`
    );
    const data = await response.json();

    if (data.status === "OK") {
      const { lat, lng } = data.results[0].geometry.location;

      const distance = getDistanceInKm(userLat, userLng, lat, lng);

      if (distance > 200) {
        toast.error(t("maps", "text2"));
        return;
      }

      setMapCenter({ lat, lng });
      getStoresNearby(lat, lng);
    } else {
      toast.error("Data not found!");
    }
  };

  // Haversine Formülü
  const getDistanceInKm = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const toRad = (value: number) => (value * Math.PI) / 180;

    const R = 6371; // Dünya yarıçapı (km)
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Mesafe km cinsinden
  };

  const getStoresNearby = async (latitude: number, longitude: number) => {
    try {

      const data = await postFetcher(endpoints.maps.getAll, {
        arg: {
          latitude,
          longitude,
          radius: 100
        }
      });
      //const filteredStores = await response.json();
      //setFilteredStores(filteredStores);
      if (data.result) {
        setFilteredStores(data.locations);
      }
    } catch (error) {
      console.error("Error fetching stores:", error);
      //toast.error("Mağaza verileri alınamadı!");
    }
  };


  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
          setMapCenter({ lat: latitude, lng: longitude });
          getStoresNearby(latitude, longitude);
        },
        (error) => {
          console.error("Error getting location: ", error);
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };


  const handleCitySearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchCity(e.target.value);
  };


  const handleCitySearchSubmit = () => {
    getCoordinatesFromCity(searchCity);
  };

  return (
    <div className="flex flex-col-reverse lg:flex-row p-2 w-12/12 xl:w-[95%] mx-auto gap-y-10 lg:gap-y-0">
      <ToastContainer />
      {/* Sol Kısım */}
      <div className="flex-1 flex justify-start items-start flex-col gap-y-6 pt-4 px-0 lg:px-10">
        <div>
          <h2 className="font-normal font-poppins text-[30px]">
            {t("maps", "text3")}
          </h2>
        </div>
        <div>
          <p className="font-normal font-poppins text-[16px]">
            {t("maps", "text4")}
          </p>
        </div>
        <div className="relative w-full max-w-md">
          <input
            type="text"
            placeholder={t("maps", "text5")}
            value={searchCity}
            onChange={handleCitySearch}
            className="w-full pl-4 pr-12 py-4 border rounded shadow-md focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
          />
          <button
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-primary-color text-white rounded p-2 hover:bg-yellow-color transition px-4"
            onClick={handleCitySearchSubmit}
          >
            {t("maps", "text6")}
          </button>
        </div>
        <div className="flex justify-center">
          <button
            className="flex items-center gap-2 px-6 py-3 text-white font-semibold rounded shadow-md transition-all duration-500 bg-gradient-to-r from-primary-color to-light-primary-color hover:from-light-primary-color hover:to-primary-color active:scale-90"
            onClick={getUserLocation}
          >
            <FaMapMarkerAlt size={20} />
            {t("maps", "text7")}
          </button>
        </div>

        <div className="w-full max-h-[390px] overflow-y-auto scroll-smooth ">
          {
            filteredStores.length > 0 && (
              filteredStores?.map((item: any, index: React.Key) => {
                return (
                  <div key={index} className="flex mb-4 gap-2 px-6 py-3 text-gray-800 font-semibold rounded shadow-md transition-all duration-500 bg-white hover:bg-gray-100 active:scale-90">
                    <div className="text-2xl">⟟</div>
                    <div className="flex flex-col w-full">
                      <h2 className="text-xl font-bold">{item.name}</h2>
                      <p className="text-sm mt-2">
                        {item.address}
                        <br />
                      </p>
                      <a href={`tel:${item.phone}`} className="text-sm mt-2 font-bold underline">{item.phone}</a>
                    </div>
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${item.lat},${item.lng}`}
                      target="_blank"
                      className="flex ml-auto justify-end text-lg cursor-pointer items-center"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="black"
                        width="48px"
                        height="48px">
                        <path d="M12 2L22 12L12 22L2 12L12 2Z" stroke="black" strokeWidth="2" fill="white" />
                        <path d="M9 12H15M15 12L12 9M15 12L12 15" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </a>
                  </div>

                )
              })
            )
          }
        </div>
      </div>

      {/* Sağ Kısım (Harita) */}
      <div className="flex-[2]">
        <LoadScript
          googleMapsApiKey={`${process.env.REACT_APP_MAPKEY}`}
          libraries={["geometry"]}
        >
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "700px" }}
            center={mapCenter}
            zoom={mapCenter.lat == 54.5 && mapCenter.lng == 15.2 ? 5 : 9}
          >

            {selectedStore && (
              <InfoWindow
                position={{
                  lat: parseFloat(selectedStore.lat),
                  lng: parseFloat(selectedStore.lng),
                }}
                onCloseClick={() => setSelectedStore(null)}
              >
                <div className="pb-3 px-3 rounded-lg shadow-md bg-white w-64">
                  <h2 className="text-lg font-semibold text-gray-800 mb-1">{selectedStore.name}</h2>
                  <p className="text-sm text-gray-600 mb-1">{selectedStore.city}, {selectedStore.country}</p>
                  <a
                    href={`tel:${selectedStore.phone}`}
                    className="block text-sm text-blue-600 font-medium underline hover:text-blue-800"
                  >
                    📞 {selectedStore.phone}
                  </a>

                  {/* Yol Tarifi Al Butonu */}
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${selectedStore.lat},${selectedStore.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 text-center w-full inline-block px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-semibold hover:bg-blue-700 transition"
                  >
                    {t("maps", "text8")}
                  </a>
                </div>
              </InfoWindow>

            )}

            {
              filteredStores?.map((store: any, index: any) => {
                return (
                  <Marker
                    key={index}
                    position={{
                      lat: parseFloat(store.lat),
                      lng: parseFloat(store.lng),
                    }}
                    label={{
                      text: store.name,
                      color: "#2A2A2A",
                      fontWeight: "bold",
                      fontSize: "12px",
                    }}
                    icon={{
                      url: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png", // Özel ikon URL'si
                      scaledSize: new window.google.maps.Size(40, 40), // İkon boyutu
                    }}
                    onClick={() => setSelectedStore(store)} // InfoWindow için
                  />
                )
              })
            }
          </GoogleMap>
        </LoadScript>
      </div>
    </div>
  );
};

export default MapComponent;